import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Types
export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
}

interface ToastContextData {
    addToast: (message: string, type?: ToastType, duration?: number) => void;
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextData>({} as ToastContextData);

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = (message: string, type: ToastType = 'info', duration = 3000) => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts((prev) => [...prev, { id, message, type, duration }]);

        if (duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }
    };

    const removeToast = (id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    return (
        <ToastContext.Provider value={{ addToast, removeToast }}>
            {children}

            {/* Toast Container */}
            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        onClick={() => removeToast(toast.id)}
                        className={`min-w-[300px] p-4 rounded-lg shadow-lg border-l-4 cursor-pointer transform transition-all duration-300 animate-in slide-in-from-right
                            ${toast.type === 'success' ? 'bg-white border-green-500 text-slate-800' :
                                toast.type === 'error' ? 'bg-white border-red-500 text-slate-800' :
                                    toast.type === 'warning' ? 'bg-white border-yellow-500 text-slate-800' :
                                        'bg-white border-blue-500 text-slate-800'
                            }
                        `}
                    >
                        <div className="flex items-center gap-3">
                            <i className={`text-xl 
                                ${toast.type === 'success' ? 'ri-checkbox-circle-fill text-green-500' :
                                    toast.type === 'error' ? 'ri-error-warning-fill text-red-500' :
                                        toast.type === 'warning' ? 'ri-alert-fill text-yellow-500' :
                                            'ri-information-fill text-blue-500'
                                }
                            `}></i>
                            <p className="text-sm font-medium">{toast.message}</p>
                        </div>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
